/* ================================================================
   Material Library
   ----------------------------------------------------------------
   Rich catalog of materials. Replaces the thin `materialKey: number`
   with a real dataset (density, price, color, mechanical props).

   - `DEFAULT_MATERIAL_LIBRARY` is the factory-shipped catalog.
   - `resolveMaterial(project, key)` falls back to the defaults if
     the project-local library is not present.
   - Pure, no side effects; callers always get a new immutable copy.
   ================================================================ */

import type { Material, ProjectModel } from '../model/types';

// --------------- Default library ---------------

/**
 * Textures are copied from the legacy standalone-3d-tool2 image set
 * into /public/textures/material/ so Vite serves them as static files.
 */
const T = (name: string) => `/textures/material/${name}.jpg`;

export const DEFAULT_MATERIAL_LIBRARY: Material[] = [
  {
    key: 1,
    name: '马尾松 (通用)',
    color: '#c8a96e',
    densityKgPerM3: 500,
    pricePerM3: 1800,
    kind: 'softwood',
    bendingStrengthMPa: 40,
    textureUrl: T('maweisong'),
    textureTileMm: 400,
    remark: '通用松木，适合普通木箱',
  },
  {
    key: 2,
    name: '花旗松',
    color: '#b08a5a',
    densityKgPerM3: 530,
    pricePerM3: 2000,
    kind: 'softwood',
    bendingStrengthMPa: 48,
    textureUrl: T('huaqisong'),
    textureTileMm: 440,
    remark: '北美进口花旗松，强度高',
  },
  {
    key: 3,
    name: '樟子松',
    color: '#d2b48c',
    densityKgPerM3: 520,
    pricePerM3: 1900,
    kind: 'softwood',
    bendingStrengthMPa: 42,
    textureUrl: T('zhangzisong'),
    textureTileMm: 400,
    remark: '俄罗斯樟子松，纹理清晰',
  },
  {
    key: 4,
    name: '辐射松 (熏蒸)',
    color: '#c99966',
    densityKgPerM3: 520,
    pricePerM3: 2100,
    kind: 'softwood',
    bendingStrengthMPa: 42,
    textureUrl: T('fushesong'),
    textureTileMm: 400,
    remark: '符合 ISPM-15 出口要求',
  },
  {
    key: 5,
    name: '落叶松',
    color: '#a67043',
    densityKgPerM3: 590,
    pricePerM3: 2100,
    kind: 'softwood',
    bendingStrengthMPa: 52,
    textureUrl: T('luoyesong'),
    textureTileMm: 400,
    remark: '硬度高，耐候性强',
  },
  {
    key: 6,
    name: '南方松',
    color: '#b58a55',
    densityKgPerM3: 560,
    pricePerM3: 1950,
    kind: 'softwood',
    bendingStrengthMPa: 50,
    textureUrl: T('nanfangsong'),
    textureTileMm: 400,
    remark: '美国南方松，受欢迎的出口木材',
  },
  {
    key: 7,
    name: '云杉',
    color: '#d6b68a',
    densityKgPerM3: 450,
    pricePerM3: 1750,
    kind: 'softwood',
    bendingStrengthMPa: 38,
    textureUrl: T('yunshan'),
    textureTileMm: 400,
    remark: '轻质，适合要求重量的包装',
  },
  {
    key: 8,
    name: '冷杉',
    color: '#cfa972',
    densityKgPerM3: 470,
    pricePerM3: 1780,
    kind: 'softwood',
    bendingStrengthMPa: 39,
    textureUrl: T('lengshan'),
    textureTileMm: 400,
  },
  {
    key: 9,
    name: '铁杉',
    color: '#9b7a4c',
    densityKgPerM3: 560,
    pricePerM3: 2100,
    kind: 'hardwood',
    bendingStrengthMPa: 55,
    textureUrl: T('tieshan'),
    textureTileMm: 400,
  },
  {
    key: 10,
    name: '橡木',
    color: '#6b4423',
    densityKgPerM3: 720,
    pricePerM3: 4500,
    kind: 'hardwood',
    bendingStrengthMPa: 75,
    textureUrl: T('yingmu'),
    textureTileMm: 480,
    remark: '高端木箱、重型设备包装',
  },
  {
    key: 11,
    name: '杨木',
    color: '#d9c291',
    densityKgPerM3: 420,
    pricePerM3: 1600,
    kind: 'hardwood',
    bendingStrengthMPa: 36,
    textureUrl: T('yangmu'),
    textureTileMm: 400,
  },
  {
    key: 12,
    name: '桦木',
    color: '#b58854',
    densityKgPerM3: 670,
    pricePerM3: 3200,
    kind: 'hardwood',
    bendingStrengthMPa: 68,
    textureUrl: T('huamu'),
    textureTileMm: 420,
  },
  {
    key: 13,
    name: '花梨木',
    color: '#7a4328',
    densityKgPerM3: 880,
    pricePerM3: 8000,
    kind: 'hardwood',
    bendingStrengthMPa: 95,
    textureUrl: T('huamu'),
    textureTileMm: 520,
    remark: '高端硬木',
  },
  {
    key: 14,
    name: '香樟',
    color: '#a7793d',
    densityKgPerM3: 600,
    pricePerM3: 3400,
    kind: 'hardwood',
    bendingStrengthMPa: 62,
    textureUrl: T('xiangmu'),
    textureTileMm: 420,
  },
  {
    key: 15,
    name: '桉木',
    color: '#a47747',
    densityKgPerM3: 780,
    pricePerM3: 2800,
    kind: 'hardwood',
    bendingStrengthMPa: 72,
    textureUrl: T('anmu'),
    textureTileMm: 420,
  },
  {
    key: 16,
    name: '硬质胶合板',
    color: '#a0522d',
    densityKgPerM3: 650,
    pricePerM3: 2600,
    kind: 'plywood',
    bendingStrengthMPa: 55,
    textureUrl: T('jhb'),
    textureTileMm: 600,
    remark: '多层胶合板，结构用板',
  },
  {
    key: 17,
    name: '包花胶合板',
    color: '#b87a3b',
    densityKgPerM3: 620,
    pricePerM3: 2400,
    kind: 'plywood',
    bendingStrengthMPa: 50,
    textureUrl: T('baohua'),
    textureTileMm: 600,
  },
  {
    key: 18,
    name: '厚木板 (HM)',
    color: '#9a6a3a',
    densityKgPerM3: 640,
    pricePerM3: 2500,
    kind: 'plywood',
    bendingStrengthMPa: 58,
    textureUrl: T('hm'),
    textureTileMm: 560,
  },
  {
    key: 19,
    name: '铝合金型材',
    color: '#c9cdd2',
    densityKgPerM3: 2700,
    pricePerM3: 24000,
    kind: 'metal',
    bendingStrengthMPa: 160,
    textureUrl: T('aluminium'),
    textureTileMm: 600,
    remark: '铝合金边框，高强度低自重',
  },
  {
    key: 20,
    name: '默认木材',
    color: '#c8a96e',
    densityKgPerM3: 500,
    pricePerM3: 1800,
    kind: 'softwood',
    textureUrl: T('defaultmaterial'),
    textureTileMm: 400,
    remark: '未设置材质时的默认回退',
  },
];

const DEFAULT_BY_KEY = new Map(DEFAULT_MATERIAL_LIBRARY.map(m => [m.key, m]));

// --------------- Accessors ---------------

export function getMaterialLibrary(project: ProjectModel): Material[] {
  return project.materialLibrary ?? DEFAULT_MATERIAL_LIBRARY;
}

export function resolveMaterial(project: ProjectModel, key: number): Material {
  const lib = project.materialLibrary;
  if (lib) {
    const hit = lib.find(m => m.key === key);
    if (hit) return hit;
  }
  return DEFAULT_BY_KEY.get(key) ?? DEFAULT_MATERIAL_LIBRARY[0];
}

export function resolveMaterialColor(project: ProjectModel, key: number): string {
  return resolveMaterial(project, key).color;
}

// --------------- Project-local library mutation helpers ---------------

/** Returns a new project with a material added/updated in its local library. */
export function upsertMaterial(project: ProjectModel, material: Material): ProjectModel {
  const lib = (project.materialLibrary ?? DEFAULT_MATERIAL_LIBRARY).slice();
  const idx = lib.findIndex(m => m.key === material.key);
  if (idx >= 0) lib[idx] = { ...material };
  else lib.push({ ...material });
  return { ...project, materialLibrary: lib };
}

export function removeMaterial(project: ProjectModel, key: number): ProjectModel {
  const lib = (project.materialLibrary ?? DEFAULT_MATERIAL_LIBRARY).filter(m => m.key !== key);
  return { ...project, materialLibrary: lib };
}
